import qs from "qs";
import {
  IAppointment,
  IDoctor,
  IDoctorBasic,
  IHealthRecord,
  IPatient,
  IPrescriptionReport,
  ITimeSlot,
  BookedSlotsData,
  IVisitInfo,
  IBranchDetails,
  SearchCriteria,
  IPaymentDetails,
} from "../types";
import { axiosInstance, localInstance } from "./axiosInstance";
import { publicAxiosInstance, publicAxiosLocalInstance } from "./publicAxiosInstance";
import { BookAppointmentSchema } from "../lib/validation/book-appointment";

interface SearchCriteriaa {
  page?: number;
  size?: number;
}

async function getDoctorsList({ page = 0, size = 10 }: SearchCriteriaa & { page?: number; size?: number; }) {
  try {
    const params: { [key: string]: string | number } = { page, size };
    const res = await publicAxiosInstance.get<{
      data: IDoctorBasic[];
      totalItems: number;
      totalPages: number;
      currentPage: number;
    }>("doctor-profile/api/doctor/api/doctor-basic-info", { params });
    return {
      data: res.data.data,
      totalItems: res.data.totalItems,
      totalPages: res.data.totalPages,
    };
  } catch (error) {
    console.error("Error fetching doctors:", error);
    throw error;
  }
}

async function getSubdistricts(speciality?: string) {
  try {
    const res = await publicAxiosInstance.get<string[]>(
      "/doctor-profile/api/doctor/subdistricts",
      {
        params: speciality ? { speciality } : {},
      }
    );
    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
}

async function getDoctorsBySearch({
  searchCriteria1 = null,
  searchCriteria2 = null,
  searchCriteria3 = null,
  page = 0,
  size = 10,
  sort = "fullName,asc",
}: SearchCriteria) {
  try {
    const params: { [key: string]: string | number } = { page, size }; if (searchCriteria1) params.speciality = searchCriteria1;
    if (searchCriteria2) params.fullName = searchCriteria2;
    if (searchCriteria3) params.subDistrictCity = searchCriteria3;

    const res = await publicAxiosInstance.get<{
      content: IDoctorBasic[];
      totalElements: number;
      totalPages: number;
      number: number;
    }>("doctor-profile/patient-profile/search-doctors", { params });

    return {
      data: res.data.content,
      totalItems: res.data.totalElements,
      totalPages: res.data.totalPages,
      currentPage: res.data.number,
    };
  } catch (error) {
    console.error("Error fetching doctors:", error);
    throw error;
  }
}




async function getPatientHealthRecords({
  name,
  mobile,
}: {
  name: string;
  mobile: string;
}) {
  try {
    const res = await axiosInstance.get<IHealthRecord[]>(
      "doctor-profile/appointment-details/health-records/name-mobile",
      {
        params: {
          name,
          mobile,
        },
      },
    );

    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
}

async function getDoctor(professionalId: string | undefined) {
  try {
    const res = await axiosInstance.get<IDoctor>(
      `doctor-profile/api/doctor/find/${professionalId}`,
    );
    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
}

async function getDoctorSlots(professionalId: string | undefined, branchId: number | undefined) {
  try {
    const res = await publicAxiosInstance.get<ITimeSlot>(
      `doctor-profile/patient-profile/professional-availability-branch1`,
      {
        params: {
          professionalId,
          branchId,
        },
      }
    );
    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
}

// Fetch booked slots for a branch
async function getBookedSlots(branchId: number | undefined) {
  try {
    const res = await publicAxiosInstance.get<BookedSlotsData>(
      `doctor-profile/patient-profile/booked-slots`,
      {
        params: { branchId },
      }
    );
    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
}

async function getReport(patientId: string, visitId: string) {
  try {
    const res = await axiosInstance.get<{
      base64: string | null;
      patientId: string;
      visitId: string;
      status: boolean;
      message: string;
    }>("doctor-profile/patient-profile/get-report", {
      params: {
        patientId,
        visitId,
      },
    });

    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
}

async function getPatientsByMobile(mobile: string | null) {
  if (!mobile) return;
  try {
    const res = await axiosInstance.get<IPatient[]>(
      "/doctor-profile/patient-profile/patient-list/by-mobile",
      {
        params: {
          mobile,
        },
      },
    );

    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
}


async function getUpcomingAppointments(
  name: string | null,
  mobile: string | null,
) {
  try {
    const res = await axiosInstance.get<IAppointment[]>(
      "doctor-profile/appointment-details/v2/get-by-mobile-name",
      {
        params: {
          name,
          mobile,
        },
      },
    );

    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
}

async function getAppointmentById(appointmentId?: Number) {
  try {
    const res = await axiosInstance.get<
      IAppointment & {
        patientName?: string;
        specialities?: string[];
        symptoms?: string[];
        clinicName?: string;
        clinicAddress?: string;
        profilePhoto?: string;
        branchDetails?: IBranchDetails[];
      }
    >(`doctor-profile/appointment-details/${appointmentId}`);

    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
}

async function getPaymentDetails(professionalId?: Number) {
  try {
    const res = await axiosInstance.get<
      IPaymentDetails & {
        upiId?: string;
        qrUploaded?: boolean;
        base64File?: string;
      }
    >(`doctor-profile/api/doctor/payment-details?professionalId=${professionalId}`);

    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
}

async function getPatientVisits(
  patientName: string | null,
  mobile: string | null,
) {
  try {
    const res = await axiosInstance.get<IVisitInfo[]>(
      `doctor-profile/patient-profile/visits/name/${patientName}/mobile/${mobile}`,
    );

    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
}

async function getDoctorSymptoms(specialityIds: number[]) {
  try {
    const res = await publicAxiosInstance.get<{
      chiefComplaints: { [key: string]: string };
    }>(
      "doctor-profile/api/doctor/symptom-list",
      {
        params: {
          specialityIds,
        },
        paramsSerializer: (params) =>
          qs.stringify(params, { arrayFormat: "repeat" }),
      },
    );

    return Object.values(res.data.chiefComplaints);
  } catch (error) {
    console.error(error);
    throw error;
  }
}

async function getPrescriptions(
  patientName: string | null | undefined,
  mobileNumber: string | null | undefined,
  uniqueCode: string | undefined,
) {
  if (!patientName || !mobileNumber || !uniqueCode) return;
  try {
    const res = await axiosInstance.get<IPrescriptionReport[]>(
      `doctor-profile/patient-profile/get-consolidated-reports/${uniqueCode}/patient`,
      {
        params: {
          patientName,
          mobileNumber,
        },
      },
    );

    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
}

async function preBookAppointment(data: BookAppointmentSchema) {
  try {
    const res = await axiosInstance.post(
      "doctor-profile/appointment-details/pre-booking",
      data,
    );
    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
}

async function bookAppointment(data: BookAppointmentSchema) {
  try {
    const res = await axiosInstance.post(
      "doctor-profile/appointment-details/patient-data",
      data,
    );
    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
}

export {
  getDoctorsList,
  getDoctorsBySearch,
  getDoctor,
  getUpcomingAppointments,
  getPatientVisits,
  getDoctorSymptoms,
  getPrescriptions,
  getPatientsByMobile,
  bookAppointment,
  preBookAppointment,
  getAppointmentById,
  getPatientHealthRecords,
  getDoctorSlots,
  getBookedSlots,
  getSubdistricts,
  getReport,
  getPaymentDetails,
};
